import React, { useState } from "react";
import { toast } from "react-toastify";
import apiServices from "../../services/service";
import FormModalWrapper from "../../shared/modal/FormModalWrapper";
import SearchableDropDown from "../../shared/controls/form/searchableDropdown";

const ACCESS_LEVEL_OPTIONS = [
  { value: "L1", label: "L1" },
  { value: "L2", label: "L2" },
  { value: "L3", label: "L3" },
];

const ACCESS_LEVEL_RANK = { L1: 1, L2: 2, L3: 3 };

const BUCKET_VISIBILITY = [
  { value: "Public", label: "Public" },
  { value: "Private", label: "Private" },
]

const AddBucketForm = ({ open, onClose, onSave, usersList = [] }) => {
  const [bucketName, setBucketName] = useState("");
  const [owner, setOwner] = useState(null);
  const [accessLevel, setAccessLevel] = useState(null);
  const [visibility, setVisibility] = useState(null);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const userOptions = usersList.map((u) => ({
    value: u.userId,
    label:
      `${u.firstName || ""} ${u.lastName || ""}`.trim() ||
      u.userName ||
      u.emailAddress ||
      u.email ||
      "",
    accessLevel: u.accessLevel,
  }));

  const isPrivate = visibility?.value === "Private";

  // Only users at or above the bucket's access level can be granted private access.
  const accessLevelRank = ACCESS_LEVEL_RANK[accessLevel?.value] ?? null;
  const assignableUserOptions = accessLevelRank
    ? userOptions.filter((u) => ACCESS_LEVEL_RANK[u.accessLevel] >= accessLevelRank)
    : userOptions;

  const reset = () => {
    setBucketName("");
    setOwner(null);
    setAccessLevel(null);
    setVisibility(null);
    setAssignedUsers([]);
    setErrors({});
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSave = () => {
    const newErrors = {};
    if (!bucketName.trim()) newErrors.bucketName = "Bucket name is required";
    if (!owner) newErrors.owner = "Bucket owner is required";
    if (!accessLevel) newErrors.accessLevel = "Access level is required";
    if (!visibility) newErrors.visibility = "Bucket visibility is required";
    if (visibility?.value === "Private" && !assignedUsers.length)
      newErrors.assignedUsers = "Please assign at least one user";
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }

    const payload = {
      name: bucketName.trim(),
      bucketOwnerId: owner?.value ?? null,
      accessLevel: accessLevel?.value ?? null,
      bucketVisibility: visibility?.value ?? null,
      userIds: visibility?.value === "Private" ? assignedUsers.map((u) => u.value).join(",") : null,
    };

    setSaving(true);
    apiServices
      .addBucket(payload)
      .then((response) => {
        toast.success("Bucket added successfully!", { position: "top-right" });
        onSave(response.data);
        reset();
      })
      .catch((error) => {
        toast.error(
          error?.response?.data?.message || error.message || "Failed to add bucket",
          { position: "top-right" }
        );
      })
      .finally(() => setSaving(false));
  };

  return (
    <FormModalWrapper
      open={open}
      onClose={handleClose}
      breadcrumb="Bucket Management"
      title="Add Bucket"
      size="sm"
      onSave={handleSave}
      saveDisabled={saving}
    >
      <div className="row">
        <div className="col-12">
          <div className="input-container">
            <label htmlFor="bucketName">
              Bucket Name <span className="required-star">*</span>
            </label>
            <input
              id="bucketName"
              className="formik-input"
              type="text"
              value={bucketName}
              onChange={(e) => {
                setBucketName(e.target.value);
                if (errors.bucketName) setErrors((prev) => ({ ...prev, bucketName: undefined }));
              }}
              placeholder="Enter bucket name"
            />
            {errors.bucketName && (
              <div className="error-message">{errors.bucketName}</div>
            )}
          </div>
        </div>
        <div className="col-12">
          <div className="input-container">
            <SearchableDropDown
              label="Access Level"
              name="accessLevel"
              fieldName="accessLevel"
              value={accessLevel}
              placeholder="Select access level"
              options={ACCESS_LEVEL_OPTIONS}
              isRequired
              error={!!errors.accessLevel}
              errormsg={errors.accessLevel}
              onChange={(selected) => {
                setAccessLevel(selected);
                if (errors.accessLevel) setErrors((prev) => ({ ...prev, accessLevel: undefined }));

                // Drop any assigned users who no longer meet the new access level's floor
                const newRank = ACCESS_LEVEL_RANK[selected?.value] ?? null;
                if (newRank) {
                  setAssignedUsers((prev) =>
                    prev.filter((u) => ACCESS_LEVEL_RANK[u.accessLevel] >= newRank)
                  );
                }
              }}
            />
          </div>
        </div>

        <div className="col-12">
          <div className="input-container">
            <SearchableDropDown
              label="Bucket Owner"
              name="owner"
              fieldName="owner"
              value={owner}
              placeholder="Select owner"
              options={userOptions}
              isRequired
              error={!!errors.owner}
              errormsg={errors.owner}
              onChange={(selected) => {
                setOwner(selected);
                if (errors.owner) setErrors((prev) => ({ ...prev, owner: undefined }));
              }}
            />
          </div>
        </div>

        <div className="col-12">
          <div className="input-container">
            <SearchableDropDown
              label="Bucket Visibility"
              name="visibility"
              fieldName="visibility"
              value={visibility}
              placeholder="Select bucket visibility"
              options={BUCKET_VISIBILITY}
              isRequired
              error={!!errors.visibility}
              errormsg={errors.visibility}
              onChange={(selected) => {
                setVisibility(selected);
                if (errors.visibility) setErrors((prev) => ({ ...prev, visibility: undefined }));
                if (selected?.value !== "Private") {
                  setAssignedUsers([]);
                  if (errors.assignedUsers) setErrors((prev) => ({ ...prev, assignedUsers: undefined }));
                }
              }}
            />
          </div>
        </div>

        {isPrivate && (
          <div className="col-12">
            <div className="input-container">
              <SearchableDropDown
                label="Assign User"
                name="assignedUsers"
                fieldName="assignedUsers"
                value={assignedUsers}
                placeholder="Select users"
                options={assignableUserOptions}
                isMulti
                isRequired
                error={!!errors.assignedUsers}
                errormsg={errors.assignedUsers}
                onChange={(selected) => {
                  setAssignedUsers(selected || []);
                  if (errors.assignedUsers) setErrors((prev) => ({ ...prev, assignedUsers: undefined }));
                }}
              />
            </div>
          </div>
        )}
      </div>
    </FormModalWrapper>
  );
};

export default AddBucketForm;
